import React from "react";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Grid,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { ExpandMore } from "@mui/icons-material";
import { PrimaryButton, SecondaryButton } from "../../../../layout/Button/Button";
import { useBranchedStoryEdit } from "../../contexts/BranchedStoryEditContext";
import { VCard } from "../../../../../core/components/VCard";
import { MappedStoryQuestionType } from "../../../../../models/modelTypes";

const useStyles = makeStyles(() =>
    createStyles({
        formContainer: {
            padding: "24px",
        },
        accordion: {
            marginBottom: "16px!important",
            border: "1px solid #e0e0e0",
        },
        accordionHeader: {
            minHeight: "64px",
        },
        partNumber: {
            backgroundColor: "#121738",
            color: "#ffffff",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "bold",
            marginRight: "12px",
        },
        questionNumber: {
            backgroundColor: "#1FAF8C",
            color: "#ffffff",
            borderRadius: "50%",
            width: "28px",
            height: "28px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            fontWeight: "bold",
            marginRight: "8px",
        },
        actionButtons: {
            marginTop: "32px",
            display: "flex",
            gap: "16px",
            justifyContent: "flex-end",
        },
        sectionTitle: {
            marginTop: "24px",
            marginBottom: "8px",
            fontWeight: "bold",
        },
        promptsList: {
            paddingLeft: "16px",
        },
    }),
);

interface EditQuestionsProps {
    onComplete: () => void;
}

interface Question {
    id: string; // Unique identifier for React keys
    partNumber: number;
    questionNumber: number;
    question: string;
    prompts: string[];
    explaination: string;
    keywordDesc: string;
    prepGuide: string;
    keywords: string[];
    helpQuestion: string;
    category: "Open" | "Closed";
}

export const EditQuestions: React.FC<EditQuestionsProps> = ({ onComplete }) => {
    const classes = useStyles();
    const { currentStory, updateQuestion, editedQuestions, addQuestion } = useBranchedStoryEdit();

    // Get questions from story parts (up to 3 questions per part, up to 7 parts = max 21 questions)
    const getQuestionsFromStory = (): Question[] => {
        const questions: Question[] = [];

        if (currentStory.StoryParts) {
            // Process each story part (up to 7 parts)
            currentStory.StoryParts.slice(0, 7).forEach((part) => {
                // Each part can have up to 3 questions
                const partQuestions = part.MappedStoryQuestion || [];

                // Process up to 3 questions per part
                partQuestions.slice(0, 3).forEach((q, index) => {
                    questions.push({
                        id: `part${part.part}_q${index + 1}`,
                        partNumber: part.part,
                        questionNumber: index + 1,
                        question: q.question || "",
                        // Parse prompts from JSON field if they exist
                        prompts: (() => {
                            try {
                                const promptJson = (q as MappedStoryQuestionType).prompt;
                                if (promptJson && typeof promptJson === "string") {
                                    const parsed = JSON.parse(promptJson);
                                    if (Array.isArray(parsed)) {
                                        // Ensure we have exactly 3 prompts for the form
                                        return [parsed[0] || "", parsed[1] || "", parsed[2] || ""];
                                    }
                                }
                            } catch (e) {
                                // If parsing fails, fall back to empty prompts
                            }
                            return ["", "", ""];
                        })(),
                        explaination: (q as MappedStoryQuestionType).explaination || "",
                        keywordDesc: (q as MappedStoryQuestionType).keywordDesc || "",
                        prepGuide: (q as MappedStoryQuestionType).prepGuide || "",
                        keywords: ((q as MappedStoryQuestionType).keywords || "")
                            .split(",")
                            .map((k: string) => k.trim())
                            .filter(Boolean),
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        helpQuestion: (q as any).hint1 || (q as any).helpQuestion || "",
                        category: (q.category as "Open" | "Closed") || "Open",
                    });
                });

                // Fill in empty questions if part has less than 3
                for (let i = partQuestions.length; i < 3; i++) {
                    questions.push({
                        id: `part${part.part}_q${i + 1}`,
                        partNumber: part.part,
                        questionNumber: i + 1,
                        question: "",
                        prompts: ["", "", ""],
                        explaination: "",
                        keywordDesc: "",
                        prepGuide: "",
                        keywords: [],
                        helpQuestion: "",
                        category: "Open",
                    });
                }
            });
        }

        return questions;
    };

    const displayQuestions = getQuestionsFromStory();

    // Get current value for a question (from edited state if available, otherwise from original)
    const getCurrentValue = (questionId: string, field: keyof Question) => {
        // Find the original question
        const originalQuestion = displayQuestions.find((q) => q.id === questionId);
        if (!originalQuestion) return "";

        // Try to find the actual database question and check for edits
        const partNum = originalQuestion.partNumber;
        const questionNum = originalQuestion.questionNumber;
        const part = currentStory.StoryParts?.find((p) => p.part === partNum);
        const dbQuestion = part?.MappedStoryQuestion?.[questionNum - 1];

        if (dbQuestion) {
            const editedQuestion = dbQuestion.id ? editedQuestions.get(dbQuestion.id) : undefined;

            if (editedQuestion) {
                // Return edited value if it exists, otherwise fall back to original
                switch (field) {
                    case "question":
                        return editedQuestion.question !== undefined
                            ? editedQuestion.question
                            : originalQuestion.question;
                    case "category":
                        return editedQuestion.category !== undefined
                            ? editedQuestion.category
                            : originalQuestion.category;
                    case "keywords":
                        return editedQuestion.keywords
                            ? editedQuestion.keywords.split(", ").filter(Boolean)
                            : originalQuestion.keywords;
                    case "prompts":
                        if (editedQuestion.prompt) {
                            try {
                                const parsed = JSON.parse(editedQuestion.prompt);
                                return Array.isArray(parsed) ? parsed : originalQuestion.prompts;
                            } catch {
                                return originalQuestion.prompts;
                            }
                        }
                        return originalQuestion.prompts;
                    case "explaination":
                        return editedQuestion.explaination !== undefined
                            ? editedQuestion.explaination
                            : originalQuestion.explaination;
                    case "keywordDesc":
                        return editedQuestion.keywordDesc !== undefined
                            ? editedQuestion.keywordDesc
                            : originalQuestion.keywordDesc;
                    case "prepGuide":
                        return editedQuestion.prepGuide !== undefined
                            ? editedQuestion.prepGuide
                            : originalQuestion.prepGuide;
                    case "helpQuestion":
                        return editedQuestion.hint1 !== undefined
                            ? editedQuestion.hint1
                            : originalQuestion.helpQuestion;
                    default:
                        return originalQuestion[field];
                }
            }
        }

        return originalQuestion[field];
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleQuestionChange = (questionId: string, field: keyof Question, value: any) => {
        // Find the database question ID
        const originalQuestion = displayQuestions.find((q) => q.id === questionId);
        if (!originalQuestion) return;

        const partNum = originalQuestion.partNumber;
        const questionNum = originalQuestion.questionNumber;
        const part = currentStory.StoryParts?.find((p) => p.part === partNum);
        const dbQuestion = part?.MappedStoryQuestion?.[questionNum - 1];

        if (dbQuestion?.id && part) {
            const updateData: Record<string, unknown> = {
                id: dbQuestion.id,
                storyPartId: part.id,
                question: dbQuestion.question,
                category: dbQuestion.category,
                order: dbQuestion.order,
            };

            // Map frontend fields to database fields
            switch (field) {
                case "question":
                    updateData.question = value;
                    break;
                case "category":
                    updateData.category = value;
                    break;
                case "keywords":
                    if (typeof value === "string") {
                        const keywordsArray = value
                            .split(",")
                            .map((k) => k.trim())
                            .filter(Boolean);
                        updateData.keywords = keywordsArray.join(", ");
                    }
                    break;
                case "prompts":
                    if (Array.isArray(value)) {
                        updateData.prompt = JSON.stringify(value);
                    }
                    break;
                case "explaination":
                    updateData.explaination = value;
                    break;
                case "keywordDesc":
                    updateData.keywordDesc = value;
                    break;
                case "prepGuide":
                    updateData.prepGuide = value;
                    break;
                case "helpQuestion":
                    updateData.hint1 = value;
                    break;
                default:
                    updateData[field] = value;
                    break;
            }

            updateQuestion(dbQuestion.id, updateData);
        }
    };

    const handlePromptChange = (questionId: string, promptIndex: number, value: string) => {
        const currentPrompts = getCurrentValue(questionId, "prompts") as string[];
        const newPrompts = [...currentPrompts];
        newPrompts[promptIndex] = value;
        handleQuestionChange(questionId, "prompts", newPrompts);
    };

    // Group questions by part for display
    const questionsByPart = displayQuestions.reduce(
        (acc, q) => {
            const key = q.partNumber;
            if (!acc[key]) acc[key] = [];
            acc[key].push(q);
            return acc;
        },
        {} as Record<number, Question[]>,
    );

    return (
        <VCard>
            <Box className={classes.formContainer}>
                <Typography variant="h4" gutterBottom>
                    Edit Questions
                </Typography>
                <Typography variant="body1" paragraph style={{ color: "#666" }}>
                    Configure up to 21 questions (3 per story part). Each question includes prompts, guidance, keywords,
                    and help text.
                </Typography>

                {/* Display questions grouped by part */}
                {Object.entries(questionsByPart).map(([partNum, questions]) => (
                    <Box key={partNum} mt={3}>
                        <Typography variant="h6" gutterBottom>
                            <span className={classes.partNumber}>{partNum}</span>
                            Story Part {partNum} Questions
                        </Typography>

                        {questions.map((question) => (
                            <Accordion key={question.id} className={classes.accordion} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionHeader}>
                                    <Box display="flex" alignItems="center" width="100%">
                                        <span className={classes.questionNumber}>Q{question.questionNumber}</span>
                                        <Box flexGrow={1}>
                                            <Typography variant="subtitle1">
                                                {question.question || `Question ${question.questionNumber} (Empty)`}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={question.category}
                                            size="small"
                                            color={question.category === "Open" ? "primary" : "default"}
                                            style={{ marginLeft: "16px" }}
                                        />
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {/* Main Question */}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Main Question"
                                                placeholder="Enter the main question text"
                                                value={getCurrentValue(question.id, "question")}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleQuestionChange(question.id, "question", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        {/* Category */}
                                        <Grid item xs={12} sm={6}>
                                            <FormControl variant="outlined" fullWidth>
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    value={getCurrentValue(question.id, "category")}
                                                    onChange={(e) =>
                                                        handleQuestionChange(question.id, "category", e.target.value)
                                                    }
                                                    label="Category"
                                                >
                                                    <MenuItem value="Open">Open</MenuItem>
                                                    <MenuItem value="Closed">Closed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Keywords */}
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Keywords"
                                                placeholder="Word1, Word2, Word3, Word4"
                                                value={(getCurrentValue(question.id, "keywords") as string[]).join(
                                                    ", ",
                                                )}
                                                fullWidth
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleQuestionChange(question.id, "keywords", e.target.value)
                                                }
                                                helperText="Comma-separated keywords"
                                            />
                                        </Grid>

                                        {/* Prompts */}
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" className={classes.sectionTitle}>
                                                Prompts / Follow-up Questions
                                            </Typography>
                                        </Grid>
                                        {[0, 1, 2].map((index) => (
                                            <Grid item xs={12} key={`prompt${index}`}>
                                                <TextField
                                                    label={`Prompt ${index + 1}${index === 2 ? " (Optional)" : ""}`}
                                                    placeholder={`Enter prompt or follow-up question ${index + 1}`}
                                                    value={
                                                        (getCurrentValue(question.id, "prompts") as string[])[index] ||
                                                        ""
                                                    }
                                                    fullWidth
                                                    variant="outlined"
                                                    onChange={(e) =>
                                                        handlePromptChange(question.id, index, e.target.value)
                                                    }
                                                />
                                            </Grid>
                                        ))}

                                        {/* Why Ask This */}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Why Ask This?"
                                                placeholder="The psychological reasoning behind asking this question"
                                                value={getCurrentValue(question.id, "explaination")}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleQuestionChange(question.id, "explaination", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        {/* Discussion Purpose */}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Discussion Purpose"
                                                placeholder="Why this conversation is important and what is expected"
                                                value={getCurrentValue(question.id, "keywordDesc")}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleQuestionChange(question.id, "keywordDesc", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        {/* Delivery Guidance */}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Delivery Guidance"
                                                placeholder="Guide for session delivery - how the group may respond and how to handle it"
                                                value={getCurrentValue(question.id, "prepGuide")}
                                                fullWidth
                                                multiline
                                                rows={2}
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleQuestionChange(question.id, "prepGuide", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        {/* Help Question */}
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Help Question"
                                                placeholder="Simplified version of question for struggling readers"
                                                value={getCurrentValue(question.id, "helpQuestion")}
                                                fullWidth
                                                variant="outlined"
                                                onChange={(e) =>
                                                    handleQuestionChange(question.id, "helpQuestion", e.target.value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        {/* Add Question button (max 3 per part) */}
                        {questions.length < 3 && (
                            <Box mt={1} mb={2} display="flex" justifyContent="center">
                                <SecondaryButton
                                    onClick={() => {
                                        const part = currentStory.StoryParts?.find((p) => p.part === parseInt(partNum));
                                        if (part) addQuestion(part.id);
                                    }}
                                >
                                    + Add Question to Part {partNum}
                                </SecondaryButton>
                            </Box>
                        )}
                    </Box>
                ))}

                {/* Show parts with no questions yet */}
                {currentStory.StoryParts?.filter(
                    (p) => !p.MappedStoryQuestion || p.MappedStoryQuestion.length === 0,
                ).map((part) => (
                    <Box key={`empty-${part.id}`} mt={3}>
                        <Typography variant="h6" gutterBottom>
                            <span className={classes.partNumber}>{part.part}</span>
                            Story Part {part.part} Questions
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            No questions yet. Add questions to this part.
                        </Typography>
                        <Box display="flex" justifyContent="center">
                            <SecondaryButton onClick={() => addQuestion(part.id)}>
                                + Add Question to Part {part.part}
                            </SecondaryButton>
                        </Box>
                    </Box>
                ))}

                {/* Action Buttons */}
                <Box className={classes.actionButtons}>
                    <SecondaryButton type="button">Skip for Now</SecondaryButton>
                    <PrimaryButton onClick={onComplete}>Continue to Review</PrimaryButton>
                </Box>
            </Box>
        </VCard>
    );
};

// Display name for debugging (following design guide)
EditQuestions.displayName = "EditQuestions";
